import { type ReactNode, useState } from "react";

import styles from "./ToolCallLine.module.css";
import { useToolCallArguments } from "../data/api/toolCalls";
import type { ToolCall } from "../data/types";
import { Icon } from "../icons";
import { cx } from "../lib/cx";
import { durationLabel } from "../lib/time";

export const TOOL_STATUS_COLOR: Record<
  NonNullable<ToolCall["status"]>,
  string
> = {
  cancelled: "var(--ink-4)",
  failed: "var(--needs-input)",
  running: "var(--pending)",
  succeeded: "var(--done)",
};

// An MCP tool arrives as `mcp__<server>__<tool>` (chat.md §Tool use). That is
// a wire name, not something to put in front of a reader: two thirds of
// `mcp__tinsmith__set_title` is punctuation and a prefix every MCP call shares.
// Both halves survive the rewrite — two servers can offer the same verb, so
// dropping the server would merge `linear: create issue` into `sentry:
// create issue` — and the verbatim name stays one hover away, because that is
// what the SDK docs and a bug report will call it.
const MCP_TOOL = /^mcp__(?<server>.+?)__(?<tool>.+)$/;

export const toolLabel = (name: string): string => {
  const parts = MCP_TOOL.exec(name)?.groups;
  return parts ? `${parts.server!}: ${parts.tool!.replace(/_+/g, " ")}` : name;
};

// The label, plus the wire name as a hover title on the calls where the two
// differ — a reader who wants to look the tool up needs the string the docs
// use, and a tooltip that just repeats `Read` is noise.
export const ToolName = ({
  className,
  name,
}: {
  readonly className: string | undefined;
  readonly name: string;
}) => {
  const label = toolLabel(name);
  return (
    <span className={className} title={label === name ? undefined : name}>
      {label}
    </span>
  );
};

// Both densities show a duration only once there is one; `status` alone marks
// a call in flight (chat.md §Tool-call cards).
export const toolDuration = (tool: ToolCall): string =>
  tool.status === "running" ? "…" : durationLabel(tool.durationMs);

/**
 * The quiet tool-call density: a flat line that opens to the call in full
 * (chat.md §Tool-call cards). Shared by the primary thread's transcript
 * (`ChatTab.tsx`) and a side thread's (`SideThreadPanel.tsx`, `compact`) —
 * both read a run of tool calls the same way, and a fork that reads a dozen
 * files should look like a list there too (side-threads.md §Data model).
 *
 * The disclosure is the caller's: it passes `onToggle` and what the row
 * expands to. Without one the row is inert and looks exactly as it did before
 * — which is what a side thread's quiet one-liners want.
 */
export const ToolCallLine = ({
  children,
  compact,
  expanded,
  more,
  onToggle,
  title,
  tool,
}: {
  readonly children?: ReactNode;
  readonly compact?: boolean;
  readonly expanded?: boolean;
  readonly more?: number;
  readonly onToggle?: () => void;
  readonly title?: string;
  readonly tool: ToolCall;
}) => {
  const status = tool.status ?? "succeeded";
  const running = status === "running";
  const row = (
    <>
      <span
        aria-label={status}
        className={cx(styles.toolLineDot, running && styles.isRunning)}
        style={{
          background: running ? "var(--accent)" : TOOL_STATUS_COLOR[status],
        }}
        title={status}
      />
      <ToolName className={styles.toolLineName} name={tool.name} />
      <span className={styles.toolLineArg} title={title ?? tool.arg}>
        {tool.arg}
        {more ? (
          <span className={styles.toolLineMore}> and {more} more</span>
        ) : null}
      </span>
      <span className={styles.toolLineDur}>{toolDuration(tool)}</span>
      {onToggle ? (
        <Icon.ChevDown
          className={styles.toolLineChev}
          size={12}
          style={{ transform: expanded ? "rotate(0)" : "rotate(-90deg)" }}
        />
      ) : null}
    </>
  );
  const className = cx(
    styles.toolLine,
    compact && styles.compact,
    status === "failed" && styles.isError,
  );
  return (
    <div className={cx(styles.toolLineWrap, compact && styles.compactWrap)}>
      {onToggle ? (
        <button
          aria-expanded={expanded ?? false}
          className={cx(className, styles.clickable)}
          onClick={onToggle}
          type="button"
        >
          {row}
        </button>
      ) : (
        <div className={className}>{row}</div>
      )}
      {expanded ? <div className={styles.toolLineOpen}>{children}</div> : null}
    </div>
  );
};

// Long enough to hold a shell script, a patch or a page of output whole. Past
// it the value is a file rather than an argument, and a hundred thousand
// characters in a <pre> costs the transcript its scroll performance for
// something nobody reads to the end.
const VALUE_CHARS = 20_000;

const asText = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value, null, 2);

// One phrasing wherever something is shown in part, whether it was the server
// that trimmed the result or the clamp above that trimmed an argument.
const trimmedNote = (shown: number, total: number): string =>
  `showing the first ${shown.toString()} of ${total.toString()} characters`;

// No hover title: the label beside the icon already reads "copy", and a
// tooltip repeating it is the noise `ToolName` above avoids for the same
// reason. The transcript's own copy button carries one because it is icon-only
// (`ChatTab.tsx`).
const CopyButton = ({ text }: { readonly text: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className={styles.detailCopy}
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      type="button"
    >
      {copied ? <Icon.Check size={11} /> : <Icon.Copy size={11} />}
      {copied ? "copied" : "copy"}
    </button>
  );
};

/**
 * One labelled value inside an open row. Two different things can cut it
 * short, and they are not the same kind of loss:
 *
 * - `total` — the server kept only the leading part of this before storing it.
 *   The rest is gone, so the clipboard cannot have it either, and the copy
 *   carries a note saying how much is missing: a trimmed log pasted into a
 *   ticket otherwise reads as the whole run.
 * - {@link VALUE_CHARS} — the render is clamped to keep a huge value from
 *   costing the transcript its scroll. Nothing is lost; copying gives all of
 *   it, so that note is a footer and the clipboard is left alone.
 */
const DetailField = ({
  label,
  text,
  total,
}: {
  readonly label: string;
  readonly text: string;
  readonly total?: number | null;
}) => {
  const shown = text.slice(0, VALUE_CHARS);
  // The length the server cut this from, or null when it kept the whole thing.
  const cut = total != null && total > text.length ? total : null;
  return (
    <div className={styles.detailField}>
      <div className={styles.detailHead}>
        <span className={styles.detailKey}>{label}</span>
        {cut === null ? null : (
          <span className={styles.detailNote}>
            {trimmedNote(text.length, cut)}
          </span>
        )}
        <CopyButton
          text={
            cut === null
              ? text
              : `${text}\n\n[… ${(cut - text.length).toString()} more characters, not stored]`
          }
        />
      </div>
      <pre className={styles.detailValue}>{shown}</pre>
      {shown.length < text.length ? (
        <div className={styles.detailNote}>
          {trimmedNote(shown.length, text.length)}
        </div>
      ) : null}
    </div>
  );
};

/**
 * What a quiet line opens to: the arguments the tool was actually called with,
 * and the result the row's header has no room for (chat.md §Tool-call cards).
 *
 * The arguments are fetched here rather than carried by the transcript, so the
 * request happens on the click that asks for them. The result is already in
 * the view model — `body_preview` rides with the card and is rewritten live as
 * the call settles — so it renders immediately and stays current.
 */
export const ToolCallDetail = ({ tool }: { readonly tool: ToolCall }) => {
  const { args, error, loading } = useToolCallArguments(tool.publicId);
  return (
    <div className={styles.detail}>
      {loading ? <div className={styles.detailNote}>loading…</div> : null}
      {error ? (
        <div className={styles.detailNote}>could not load the arguments</div>
      ) : null}
      {/* The argument the header already shows whole is dropped: `Read` has
          one, and repeating the path under the row carrying it is a field
          that only ever says "yes, that path". A command longer than the
          summary differs from it, so it survives — which is the case the row
          is opened for. */}
      {args
        ? Object.entries(args)
            .filter(([, value]) => value !== tool.arg)
            .map(([key, value]) => (
              <DetailField key={key} label={key} text={asText(value)} />
            ))
        : null}
      {tool.body ? (
        <DetailField label="result" text={tool.body} total={tool.bodyLength} />
      ) : null}
      {/* A call that returned nothing is not the same as one whose result is
          still on its way, and the difference is the whole of what a reader
          who opened the row is waiting on. */}
      {!tool.body && !loading ? (
        <div className={styles.detailNote}>
          {tool.status === "running"
            ? "still running — no result yet"
            : "no result"}
        </div>
      ) : null}
    </div>
  );
};
